import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import { useState, useEffect, useMemo } from "react";
import {
  Box, Button, TextField, Typography, Paper, useTheme, IconButton, Dialog, InputAdornment
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TitleHeader from "../../components/TitleHeader";
import Loading from "../../components/Loading";
import useToast from "../../components/useToast";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import dayjs from "dayjs";
import "../../i18n";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

/* ---------- time utils ---------- */
const toDayjs = (s) => (s ? dayjs(s, "HH:mm") : null);
const fromDayjs = (v) => (v?.isValid() ? v.format("HH:mm") : "");
const timeToMin = (t) => {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

/* ---------- opening hours helpers ---------- */
const hasBreakForRow = (i, vals) => Boolean(vals[`day_${i}_2`] || vals[`day_${i}_3`]);

const addBreakRow = (i, setFormData) =>
  setFormData((p) => {
    const next = { ...p };
    const close = next[`day_${i}_3`] || next[`day_${i}_1`] || "";
    next[`day_${i}_3`] = close; // final close in break-slot
    next[`day_${i}_1`] = "";    // break start empty
    next[`day_${i}_2`] = "";    // break end empty
    return next;
  });

const removeBreakRow = (i, setFormData) =>
  setFormData((p) => {
    const next = { ...p };
    const finalClose = next[`day_${i}_3`] || next[`day_${i}_1`] || "";
    next[`day_${i}_1`] = finalClose; // single-slot model
    next[`day_${i}_2`] = "";
    next[`day_${i}_3`] = "";
    return next;
  });

const collapseBreakIfEmptyRow = (i, vals, setFormData) => {
  const bStart = vals[`day_${i}_1`];
  const bEnd = vals[`day_${i}_2`];
  const hasWithBreakClose = vals[`day_${i}_3`];
  if (!bStart && !bEnd && hasWithBreakClose) {
    removeBreakRow(i, setFormData);
  }
};

/* ---------- validators ---------- */
const openCloseInvalid = (open, close) => {
  const o = timeToMin(open);
  const c = timeToMin(close);
  if (o == null || c == null) return false;
  return o === c; // nur Gleichheit ist "invalid"
};

const breakInvalid = (open, close, bStart, bEnd) =>
  open && close && bStart && bEnd &&
  [open, bStart, bEnd, close].every((x) => timeToMin(x) != null)
    ? !(timeToMin(open) < timeToMin(bStart) &&
        timeToMin(bStart) < timeToMin(bEnd) &&
        timeToMin(bEnd) < timeToMin(close))
    : false;



/* Farblogik analog Dashboard-Avatar */
const baseColorFromString = (str, theme) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const palette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];
  return palette[Math.abs(hash) % palette.length];
};

const DeptTag = ({ label, onDelete }) => {
  const theme = useTheme();
  const base = useMemo(() => baseColorFromString(label, theme), [label, theme.palette.mode]);
  const bg = alpha(base, theme.palette.mode === "dark" ? 0.24 : 0.16);
  const borderCol = alpha(base, theme.palette.mode === "dark" ? 0.50 : 0.28);

  return (
    <Box sx={{ mr: 1, mb: 1 }}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1.25,
          height: 32,
          borderRadius: 16,
          bgcolor: bg,
          color: base,
          border: `1px solid ${borderCol}`,
          boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
          fontWeight: 600,
          userSelect: "none",         // kein Pointer, kein Klick-Handler
        }}
        title={label}
      >
        <Typography variant="body2" sx={{ mr: 0.25, lineHeight: 1 }}>
          {label}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
          sx={{ color: base, opacity: 0.7, "&:hover": { opacity: 1 } }}
        >
          <DeleteRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

/* ---------- helper: grauer Hintergrund für leere Zeitfelder ---------- */
const emptyFieldSx = (hasValue) => (theme) => ({
  bgcolor: hasValue ? "transparent" : theme.palette.action.hover,
  "& .MuiOutlinedInput-input::placeholder": { opacity: 0.8 },
});

const QuickStartPopup = ({ open, onClose }) => {
  const token = localStorage.getItem("session_token");
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success, error } = useToast();

  /* --- Wizard State --- */
  const [currentStep, setCurrentStep] = useState(0); // 0=Welcome, 1=Wochenstunden, 2=Fähigkeiten, 3=Öffnungszeiten
  const [weekdays, setWeekdays] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* --- Form State --- */
  const [formData, setFormData] = useState({
    weekly_hours: "",
    working_days_per_week: "",
    // departments
    department: "",
    department2: "",
    department3: "",
    department4: "",
    department5: "",
    department6: "",
    department7: "",
    department8: "",
    department9: "",
    department10: "",
    // opening times
    day_0_0: "", day_0_1: "", day_0_2: "", day_0_3: "",
    day_1_0: "", day_1_1: "", day_1_2: "", day_1_3: "",
    day_2_0: "", day_2_1: "", day_2_2: "", day_2_3: "",
    day_3_0: "", day_3_1: "", day_3_2: "", day_3_3: "",
    day_4_0: "", day_4_1: "", day_4_2: "", day_4_3: "",
    day_5_0: "", day_5_1: "", day_5_2: "", day_5_3: "",
    day_6_0: "", day_6_1: "", day_6_2: "", day_6_3: "",
  });

  /* --- Departments UI State --- */
  const [departments, setDepartments] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(-1);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    setWeekdays(["Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag","Sonntag"]);
  }, []);

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));
  const handleInputChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  /* --- Step Validation --- */
  const isStep1Valid = () => (formData.weekly_hours || "").toString().trim() !== "";
  const isStep2Valid = () => departments.length >= 1;

  /* --- Submit --- */
  const handleSubmit = async () => {
    // auto-collapse any empty breaks
    const normalized = { ...formData };
    for (let i = 0; i < 7; i++) {
      const bStart = normalized[`day_${i}_1`];
      const bEnd   = normalized[`day_${i}_2`];
      const hasWithBreakClose = normalized[`day_${i}_3`];
      if (!bStart && !bEnd && hasWithBreakClose) {
        normalized[`day_${i}_1`] = normalized[`day_${i}_3`];
        normalized[`day_${i}_2`] = "";
        normalized[`day_${i}_3`] = "";
      }
    }

    // Departments als Array für das Backend
    const departmentsPayload = departments
      .map((d) => (d || "").trim())
      .filter(Boolean);

    // payload: leere Strings -> undefined
    const payload = { ...normalized, departments: departmentsPayload };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" || payload[k] === undefined) payload[k] = undefined;
    });

    try {
      setIsSubmitting(true);
      await axios.post(`${API_BASE_URL}/api/check_initial_setup`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      success(t("notification.successful", "Erfolgreich gespeichert."));
      onClose?.();
      navigate("/welcome");
    } catch (e) {
      error(t("registration.no_success", "Registrierung fehlgeschlagen."));
    } finally {
      setIsSubmitting(false);
    }
  };

  /* --- Overlay: füllt Viewport --- */
  const overlayStyle = {
    background: alpha(theme.palette.common.black, 0.35),
    backdropFilter: "blur(2px)",
    zIndex: (theme.zIndex?.modal ?? 1300) + 2,
    pointerEvents: "auto",
    height: "100dvh",
    width: "100vw",
  };

  /* --- Content: überschreibt reactjs-popup Default-Centering --- */
  const contentStyle = {
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    position: "static",
    transform: "none",
    inset: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  };

  /* --- Titel + Untertitel (mit deutschen Fallbacks) --- */
  const titles = {
    0: t("quickstart.welcomeTitle", "Herzlich willkommen bei TimeTab!"),
    1: t("quickstart.weeklyTitle", "Schritt 1: Wochenarbeitszeit"),
    2: t("quickstart.deptTitle", "Schritt 2: Fähigkeiten/Abteilungen"),
    3: t("quickstart.openingTitle", "Schritt 3: Öffnungszeiten"),
  };
  const subtitles = {
    0: t("quickstart.welcomeSubtitle", "Bevor du loslegst, lass uns gemeinsam die ersten Einstellungen vornehmen."),
    1: t("quickstart.weeklySubtitleDetailed",
         "Gib die vertragliche Wochenarbeitszeit für Vollzeit an (z. B. 42). Diese Angabe hilft bei fairer Stundenverteilung und kann später jederzeit angepasst werden."),
    2: t("quickstart.deptSubtitle",
         "Lege deine Fähigkeiten/Abteilungen fest (z. B. Service, Bar, Küche). Du kannst sie später jederzeit bearbeiten."),
    3: t("quickstart.openingSubtitle",
         "Wenn du für einen Tag keine Zeiten einträgst, gilt dieser als „Geschlossen“."),
  };

  /* --- Departments handlers --- */
  const openEdit = (idx, initial) => { setEditIdx(idx); setEditValue(initial); setEditOpen(true); };
  const closeEdit = () => { setEditOpen(false); setEditIdx(-1); setEditValue(""); };

  // MINIMAL-ÄNDERUNG: liefert true/false
  const handleAddDeptAndSubmit = () => {
    const name = (newDept || "").trim();
    if (!name) return true; // nichts zu tun -> ok

    if (departments.some((d) => d.toLowerCase() === name.toLowerCase())) {
      error(t("company.error_department_exists", "Abteilung existiert bereits."));
      return false;
    }

    setDepartments((prev) => [...prev, name]);
    setNewDept("");
    return true;
  };

  const saveEdit = () => {
    const name = (editValue || "").trim();
    if (!name) {
      return error(t("company.error_name_required", "Name darf nicht leer sein."));
    }

    if (departments.some((d, i) => i !== editIdx && d.toLowerCase() === name.toLowerCase())) {
      return error(t("company.error_department_exists", "Abteilung existiert bereits."));
    }

    setDepartments((prev) =>
      prev.map((d, i) => (i === editIdx ? name : d))
    );
    closeEdit();
  };
  

  const handleDeleteDepartment = (idxToRemove) => {
    setDepartments((prev) => prev.filter((_, i) => i !== idxToRemove));
  };


  // MINIMAL-ÄNDERUNG: offene Eingabe bei Schritt 2 automatisch übernehmen
  const handleNext = () => {
    if (currentStep === 2) {
      const ok = handleAddDeptAndSubmit();
      if (!ok) return;
    }
    setCurrentStep((s) => Math.min(s + 1, 3));
  };

  return (
    <Popup
      open={open}
      modal
      lockScroll
      closeOnDocumentClick={false}
      closeOnEscape={false}
      onClose={onClose}
      overlayStyle={overlayStyle}
      contentStyle={contentStyle}
    >
      <Paper
        sx={{
          /* Höhe zuverlässig im Viewport halten + scrollen */
          maxHeight: { xs: "calc(100dvh - 32px)", sm: "calc(100dvh - 64px)" },
          overflowY: "auto",

          p: { xs: 2, md: 3 },
          borderRadius: 4,
          boxShadow: `0 12px 30px ${alpha(theme.palette.common.black, 0.25)}`,
          bgcolor: "background.paper",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,

          /* Breite responsiv – auf kleinen Screens voll nutzen */
          width: { xs: "calc(100% - 32px)", sm: "min(92vw, 980px)" },
          maxWidth: 980,

          position: "relative",
        }}
      >
        <Loading open={isSubmitting} />

        <TitleHeader
          title={titles[currentStep]}
          subtitle={subtitles[currentStep]}
          actions={
            <Box sx={{ display: "flex", gap: 1 }}>
              {currentStep > 0 && (
                <Button variant="outlined" onClick={handleBack} sx={{ borderRadius: 999 }}>
                  {t("button.back", "Zurück")}
                </Button>
              )}
              {currentStep < 3 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  // MINIMAL-ÄNDERUNG: Schritt 2 erlaubt Weiter, wenn newDept bereits Text hat
                  disabled={
                    (currentStep === 1 && !isStep1Valid()) ||
                    (currentStep === 2 && !isStep2Valid() && !(newDept || "").trim())
                  }
                  sx={{ borderRadius: 999 }}
                >
                  {t("button.next", "Weiter")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  sx={{ borderRadius: 999 }}
                >
                  {t("quickstart.startNow", "Starte jetzt mit TimeTab")}
                </Button>
              )}
            </Box>
          }
        />

        {/* Step indicators */}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mb: 2 }}>
          {[0,1,2,3].map((s) => (
            <Box
              key={s}
              sx={{
                height: 8,
                width: currentStep === s ? 40 : 12,
                borderRadius: 999,
                transition: "all 160ms ease",
                bgcolor: currentStep === s ? "primary.main" : alpha(theme.palette.text.primary, 0.2),
              }}
            />
          ))}
        </Box>

        {/* STEP 0: Welcome mit Logo (ohne Radius) */}
        {currentStep === 0 && (
          <Box sx={{ px: { xs: 1, md: 2 }, pt: 1, pb: 6 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 3, md: 5 } }}>
              <Box
                component="img"
                src="/assets/TimeTab_Logo_quer_cmyk_pos.png"
                alt={t("quickstart.logoAlt", "TimeTab Logo")}
                sx={{ width: { xs: 200, md: 280 }, height: "auto" }}
              />
            </Box>
            <Typography
              variant="body1"
              sx={{ textAlign: "center", color: "text.secondary", mt: 3 }}
            >
              {t("quickstart.welcomeHint", "Nimm dir hierfür maximal 5 Minuten und du bist startklar!")}
            </Typography>
          </Box>
        )}

        {/* STEP 1: Wochenarbeitszeit */}
        {currentStep === 1 && (
          <Box sx={{ px: { xs: 1, md: 2 }, pt: 1, pb: 6 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 1.5, alignItems: "center" }}>
              <TextField
                variant="outlined"
                type="number"
                inputProps={{ min: 0, step: 0.25 }}
                name="weekly_hours"
                value={formData.weekly_hours}
                onChange={handleInputChange}
                placeholder="42"
                sx={{ width: { xs: "60%", sm: 260 } }}
                label={t("company.weekhours", "Wochenstunden")}
              />
              <Typography color="text.secondary">{t("company.hours_label", "Stunden")}</Typography>
            </Box>
          </Box>
        )}

        {/* STEP 2: Fähigkeiten/Abteilungen */}
        {currentStep === 2 && (
          <Box sx={{ px: { xs: 1, md: 2 }, pt: 1, pb: 6 }}>
            {/* Eingabe + Add (präsenter) */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, justifyContent: "center", mt: 2, flexDirection: "column" }}>
              <TextField
                size="small"
                label={t("company.add_department", "Abteilung hinzufügen")}
                value={newDept}
                onChange={(e) => setNewDept(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddDeptAndSubmit();
                  }
                }}
                sx={{ width: { xs: "100%", sm: 420 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddDeptAndSubmit}
                        startIcon={<AddRoundedIcon />}
                        sx={{
                          ml: 1,
                          "@keyframes pulse": {
                            "0%": { transform: "scale(1)" },
                            "50%": { transform: "scale(1.07)" },
                            "100%": { transform: "scale(1)" },
                          },
                          animation: (newDept || "").trim() ? "pulse 1.1s ease-in-out infinite" : "none",
                        }}
                      >
                        {t("company.add", "Hinzufügen")}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, textAlign: "center" }}
              >
                {t("company.add_department_hint", "Drücke Enter oder klicke auf „Hinzufügen“, um die Fähigkeit zu übernehmen.")}
              </Typography>
            </Box>

            {/* Tags */}
            <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
              {departments.map((dept, idx) => (
                <DeptTag
                  key={`dept_${dept}_${idx}`}
                  label={dept}
                  onDelete={() => handleDeleteDepartment(idx)}
                />
              ))}
              {departments.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t("company.add_first_department_hint", "Füge mindestens eine Fähigkeit/Abteilung hinzu.")}
                </Typography>
              )}
            </Box>

            {/* Edit Dialog */}
            <Dialog
              open={editOpen}
              onClose={closeEdit}
              PaperProps={{ sx: { borderRadius: 2, p: 0.5 } }}
            >
              <Box m={2}>
                <TitleHeader
                  title={t("company.edit_department", "Abteilung bearbeiten")}
                  actions={
                    <Button variant="contained" onClick={saveEdit}>
                      {t("button.submit", "Speichern")}
                    </Button>
                  }
                />
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEdit();
                    if (e.key === "Escape") closeEdit();
                  }}
                />
              </Box>
            </Dialog>
          </Box>
        )}

        {/* STEP 3: Öffnungszeiten (Default „Geschlossen“), leere Felder grau */}
        {currentStep === 3 && (
          <Box sx={{ px: { xs: 1, md: 2 }, pt: 1, pb: 6 }}>
            <Box
              sx={{
                mt: 2,
                display: "grid",
                gap: 1.25,
                gridTemplateColumns: "repeat(5, minmax(0,1fr))",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {Array.from({ length: 7 }).map((_, i) => {
                  const hasBreak = hasBreakForRow(i, formData);

                  const openVal  = formData[`day_${i}_0`] || "";
                  const closeVal = (hasBreak ? formData[`day_${i}_3`] : formData[`day_${i}_1`]) || "";
                  const bStart   = formData[`day_${i}_1`] || "";
                  const bEnd     = formData[`day_${i}_2`] || "";

                  const isClosed = !openVal && !closeVal;
                  const badOpenClose = openCloseInvalid(openVal, closeVal);
                  const badBreak     = hasBreak && breakInvalid(openVal, closeVal, bStart, bEnd);

                  return (
                    <Box key={`row-${i}`} sx={{ display: "contents" }}>
                      {/* Weekday */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          color: "text.primary",
                        }}
                      >
                        {weekdays[i]}
                      </Box>

                      {/* Open */}
                      <TimeField
                        label={t("company.startime1", "Öffnet")}
                        format="HH:mm"
                        value={toDayjs(openVal)}
                        onChange={(v) => setFormData((p) => ({ ...p, [`day_${i}_0`]: fromDayjs(v) }))} 
                        placeholder="hh:mm"
                        slotProps={{ textField: {
                          fullWidth: true, variant: "outlined", error: badOpenClose,
                          sx: emptyFieldSx(Boolean(openVal))
                        }}}
                      />

                      {/* Close (single-slot or with-break close) */}
                      <TimeField
                        label={t("company.endtime1", "Schließt")}
                        format="HH:mm"
                        value={toDayjs(closeVal)}
                        onChange={(v) =>
                          setFormData((p) => ({
                            ...p,
                            [hasBreak ? `day_${i}_3` : `day_${i}_1`]: fromDayjs(v),
                          }))
                        }
                        placeholder="hh:mm"
                        slotProps={{ textField: {
                          fullWidth: true, variant: "outlined", error: badOpenClose,
                          sx: emptyFieldSx(Boolean(closeVal))
                        }}}
                      />

                      {/* Actions + „Geschlossen“-Badge */}
                      <Box sx={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: 1, minHeight: 40 }}>
                        {isClosed && (
                          <Box
                            sx={{
                              px: 1,
                              height: 28,
                              display: "inline-flex",
                              alignItems: "center",
                              borderRadius: 1,
                              bgcolor: (th) => alpha(th.palette.error.main, 0.18),
                              color: "error.main",
                              fontWeight: 700,
                              fontSize: 12,
                              border: (th) => `1px solid ${alpha(th.palette.error.main, 0.35)}`
                            }}
                          >
                            {t("company.closed", "Geschlossen")}
                          </Box>
                        )}

                        {!hasBreak ? (
                          <Button
                            size="small"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => addBreakRow(i, setFormData)}
                            disabled={!openVal || !closeVal}
                          >
                            {t("company.add_break", "Pause hinzufügen")}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="warning"
                            startIcon={<RemoveCircleOutlineIcon />}
                            onClick={() => removeBreakRow(i, setFormData)}
                          >
                            {t("company.remove_break", "Pause entfernen")}
                          </Button>
                        )}
                      </Box>

                      {/* Break row */}
                      {hasBreak && (
                        <Box sx={{ gridColumn: "1 / -1", mt: 0.5 }}>
                          <Box
                            sx={{
                              display: "grid",
                              gap: 1,
                              gridTemplateColumns: "repeat(5, minmax(0,1fr))",
                              p: 1,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}
                            >
                              {t("company.break", "Pause")}
                            </Typography>

                            <TimeField
                              label={t("company.startime2", "Pausenbeginn")}
                              format="HH:mm"
                              value={toDayjs(bStart)}
                              onChange={(v) => setFormData((p) => ({ ...p, [`day_${i}_1`]: fromDayjs(v) }))} 
                              onBlur={() => collapseBreakIfEmptyRow(i, formData, setFormData)}
                              placeholder="hh:mm"
                              slotProps={{ textField: {
                                fullWidth: true, variant: "outlined", error: badBreak,
                                sx: emptyFieldSx(Boolean(bStart))
                              }}}
                            />

                            <TimeField
                              label={t("company.endtime2", "Pausenende")}
                              format="HH:mm"
                              value={toDayjs(bEnd)}
                              onChange={(v) => setFormData((p) => ({ ...p, [`day_${i}_2`]: fromDayjs(v) }))} 
                              onBlur={() => collapseBreakIfEmptyRow(i, formData, setFormData)}
                              placeholder="hh:mm"
                              slotProps={{ textField: {
                                fullWidth: true, variant: "outlined", error: badBreak,
                                sx: emptyFieldSx(Boolean(bEnd))
                              }}}
                            />

                            <Box />
                            <Box />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </LocalizationProvider>
            </Box>
          </Box>
        )}
      </Paper>
    </Popup>
  );
};

export default QuickStartPopup;
